import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Tour } from '../types';

interface TourOrderContextType {
  featuredTours: Tour[];
  setFeaturedTours: (tours: Tour[]) => void;
  reorderTours: (fromIndex: number, toIndex: number) => void;
  loading: boolean;
}

const TourOrderContext = createContext<TourOrderContextType | undefined>(undefined);

export const useTourOrder = () => {
  const context = useContext(TourOrderContext);
  if (context === undefined) {
    throw new Error('useTourOrder must be used within a TourOrderProvider');
  }
  return context;
};

interface TourOrderProviderProps {
  children: ReactNode;
}

export const TourOrderProvider: React.FC<TourOrderProviderProps> = ({ children }) => {
  const [featuredTours, setFeaturedToursState] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const isInternalUpdateRef = useRef(false); // Flag to prevent infinite loops

  // Load initial tours and any saved order
  useEffect(() => {
    let handleToursUpdate: (() => Promise<void>) | null = null;
    let refreshTimeout: NodeJS.Timeout | null = null;

    const loadTours = async () => {
      try {
        const { tourService } = await import('../services/tourService');

        // Get tours (will return cached data immediately or empty array)
        const allTours = await tourService.getAllTours(false);
        const activeTours = allTours.filter(tour => tour.active);

        // Process and set tours immediately (even if empty)
        const processTours = (tours: Tour[]) => {
          // Use backend sortOrder for the featured tours (first 3)
          // Sort explicitly by sortOrder ascending, falling back to 0
          const sortedTours = [...tours].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
          isInternalUpdateRef.current = true;
          setFeaturedToursState(sortedTours.slice(0, 3));
        };

        processTours(activeTours);
        setLoading(false);

        // Listen for live tour updates (e.g. after admin saves a tour)
        const onToursUpdated = async () => {
          try {
            const refreshed = await tourService.getActiveTours(true);
            if (refreshed.length > 0) processTours(refreshed);
          } catch {}
        };
        window.addEventListener('toursUpdated', onToursUpdated);

        // If we got empty array, tours are being fetched in background
        if (allTours.length === 0) {
          // If we got empty array, tours are being fetched in background
          // We don't listen to toursUpdated here to avoid recursion

          // Also try to refresh after a short delay
          refreshTimeout = setTimeout(async () => {
            const refreshedTours = await tourService.getActiveTours();
            if (refreshedTours.length > 0) {
              processTours(refreshedTours);
            }
          }, 2000);
        } else {
          // We have cached data, refresh in background if needed
          const currentTours = activeTours;
          tourService.getActiveTours().then(refreshedTours => {
            if (refreshedTours.length !== currentTours.length ||
              refreshedTours.some((t, i) => t.id !== currentTours[i]?.id)) {
              processTours(refreshedTours);
            }
          }).catch(() => {
            // Silently fail - we already have cached data
          });
        }
      } catch (error) {
        console.error('Error loading tours for order context:', error);
        isInternalUpdateRef.current = true; // Mark as internal update
        setFeaturedToursState([]);
        setLoading(false);
      }

      // Reset flag after state update
      setTimeout(() => {
        isInternalUpdateRef.current = false;
      }, 0);
    };

    loadTours();

    // No longer listening to localStorage for featuredToursOrder

    return () => {
      if (handleToursUpdate) {
        window.removeEventListener('toursUpdated', handleToursUpdate);
      }
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, []);

  // Order saving is now handled explicitly by the reorder components
  // to prevent recursive updates and conflicts between category-based 
  // and global-based ordering.

  const setFeaturedTours = (tours: Tour[]) => {
    setFeaturedToursState(tours);
  };

  const reorderTours = (fromIndex: number, toIndex: number) => {
    const newTours = [...featuredTours];
    const [movedTour] = newTours.splice(fromIndex, 1);
    newTours.splice(toIndex, 0, movedTour);
    setFeaturedToursState(newTours);
  };

  const value: TourOrderContextType = {
    featuredTours,
    setFeaturedTours,
    reorderTours,
    loading
  };

  return (
    <TourOrderContext.Provider value={value}>
      {children}
    </TourOrderContext.Provider>
  );
};

export default TourOrderContext;
